import { call, put, select, takeEvery } from 'redux-saga/effects'
import { get, isEmpty } from 'lodash'
import { toast } from 'react-toastify'

import {
  addToUnsyncedOrderList,
  applyOrderExemptions,
  closeTabOrderSucceeded,
  saveOrder,
  START_TAB_WITH_REMOTE_PAYMENT,
  syncOrderInitiated,
  syncOrderRequested,
  updateOrderExemptionOverrides,
  reopenOrder,
  updateOrderLineItemsSucceeded,
  updateOrderLineItemsRequested,
  orderDetailsRequested,
  addPaymentOnOrder,
} from '../../actions/order'

import {
  getReceiptDetails,
  receiptDetailsSucceeded,
  receiptDetailsFailed,
} from '../../actions/receipt'

import { createOrder } from '../../models/order/create'

import { makeGetOrder, getFinalOrderState } from '../../selectors/order'
import { makeGetCartItems } from '../../selectors/cart'

import { ORDER_STATES } from '../../utils/orderStates'
import { resolveNextAmountInCents } from '../../utils/orderUtils'
import PaymentTypes from '../../utils/paymentTypes'
import Log from '../../utils/Log'

import MutationApi from '../../remote/mutationApi'
import Remote from '../../remote'

import { customHistory } from '../../stores'

const getOrder = makeGetOrder()

const directTo = (method = 'push', path) => {
  if (path === undefined) return

  customHistory[method](path)
}

const customerPaymentData = (paymentPreference) => {
  const customerPaymentId = paymentPreference.uuid
  const paymentType = PaymentTypes.nameFromInteger(paymentPreference.paymentType) || 'cash'

  let paymentDisplay = paymentPreference.shortDescription

  if (PaymentTypes.isMobileWalletPayment(paymentType)) paymentDisplay = paymentDisplay ?? 'Mobile Pay'

  // NOTE (westinschepper): This is temporary
  if (PaymentTypes.isDirectBill(paymentType)) paymentDisplay = 'Payment Pending'
  if (PaymentTypes.isHouseAccount(paymentType)) paymentDisplay = 'House Account'
  if (PaymentTypes.isFnbCredit(paymentType)) paymentDisplay = 'F&B Credit'

  return { customerPaymentId, paymentDisplay, paymentType }
}

export function* startRemotePaymentOrder(params = {}) {
  const paymentPreference = get(params, 'payload.paymentPreference', {})
  const getCartItems = makeGetCartItems()
  const lineItems = yield select(getCartItems)

  const args = {
    exemptionOverrides: get(params, 'payload.exemptionOverrides'),
    isRemotePayment: true,
    state: ORDER_STATES['TABBED'],
    uuid: get(params, 'payload.orderId'),
    userNotes: params?.payload?.userNotes ?? null,
    ...customerPaymentData(paymentPreference),
  }

  if (isEmpty(lineItems)) {
    yield call(directTo, 'push', '/tab-order')
    toast.error("Orders must have at least one associated item")
    return
  }
  
  const order = yield createOrder(args)

  if (order.remoteOrderTotal.failed) {
    toast.error("Order Creation Failed")
    return
  }
  const { uuid } = order

  yield put(saveOrder(order))
  yield put(addToUnsyncedOrderList(uuid))
  yield put(syncOrderInitiated(uuid))
  yield put(syncOrderRequested(uuid))
}

export function* watchStartRemotePaymentOrder() {
  yield takeEvery(START_TAB_WITH_REMOTE_PAYMENT, startRemotePaymentOrder)
}


export function* closeTabWithRemotePayment(params) {
  const orderId = get(params, 'payload.orderId', '')
  const order = yield select(getOrder, orderId)
  const deferClose = get(params, 'payload.deferClose', false)
  const finalOrderState = yield select(getFinalOrderState, order)
  const { amountInCents, createOrderParams } = order
  const receiptSignature = createOrderParams?.receiptSignature
  const tipAmountInCents = createOrderParams?.tipAmountInCents
  const paymentPreference = get(params, 'payload.paymentPreference', {})
  const { customerPaymentId, paymentDisplay, paymentType } = customerPaymentData(paymentPreference)
  const payments = [{ amountInCents, customerPaymentId, paymentType }]
  const isNewPayment = customerPaymentId !== order?.customerPaymentId

  // JEFF: adding this just to keep in sync with EC
  if (isNewPayment && deferClose) {
    yield put(saveOrder({
      isRemotePayment: true,
      paymentDisplay,
      state: deferClose ? ORDER_STATES['TABBED'] : finalOrderState,
      uuid: orderId,
      customerPaymentId,
      isNewPayment,
      paymentInfo: [{
        paymentType,
        customerPaymentId,
        amountInCents,
      }]
    }))

    let payload = {
      orderId,
      payments,
    }

    yield put(addPaymentOnOrder(payload))
  }

  if (!deferClose) {
    try {
      let payload = {
        orderId,
        receiptSignature,
        tipAmountInCents,
      }

      const result = yield call(MutationApi.closeTabOrder, payload)
      const applied = result?.data?.orderMutations?.[0]?.applied
      if (applied) {
        yield call(directTo, 'push', `/receipt/${orderId}`)
        if (order?.isNewPayment) {
          yield put(saveOrder({uuid: orderId, isNewPayment: false}))
        }
        yield put(closeTabOrderSucceeded(orderId, result)) // currently does nothing
      } else {
        yield call(directTo, 'push', '/tabs')
      }
    } catch (err) {
      yield call(directTo, 'push', '/tabs')
      const errorMessage = err?.response?.data?.errorMessage ?? 'Sorry, but something went wrong'
      Log.error('Close Tab Order Failed', { errorMessage, orderId })
    }
  }
}

export function* applyOrderExemptionsSaga(params = {}) {
  const orderId = get(params, 'payload.orderId')
  const exemptionOverrides = get(params, 'payload.exemptionOverrides', {})
  const order = yield select(getOrder, orderId)

  const orderTotal = order?.orderTotalFromOrder ?? {}
  const amountInCents = orderTotal?.totalAmountInCents ?? 0
  const nextAmountInCents = resolveNextAmountInCents(amountInCents, orderTotal, exemptionOverrides)

  yield put(updateOrderExemptionOverrides(orderId, exemptionOverrides))
  yield put(saveOrder({ uuid: orderId, amountInCents: nextAmountInCents }))
}

export function* watchApplyOrderExemptionsSaga() {
  yield takeEvery(applyOrderExemptions.type, applyOrderExemptionsSaga)
}

export function* reopenClosedOrder(params = {}) {
  const orderId = params.payload
  try {
    const result = yield call(MutationApi.reopenOrder, orderId)
    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield put(orderDetailsRequested(orderId))
    }
  } catch (err) {
    const errorMessage = err?.response?.data?.errorMessage ?? "We're sorry but this order could not be re-opened"
    Log.error('ReOpen Order Failed', { errorMessage, orderId })
  }
}

export function* watchReopenOrderSaga() {
  yield takeEvery(reopenOrder.type, reopenClosedOrder)
}

export function* updateOrderLineItems(params = {}) {
  const orderId = get(params, 'payload.orderId')
  const orderMenuItems = get(params, 'payload.orderMenuItems')
  const totalQuantityUpdated = get(params, 'payload.totalQuantityUpdated')
  try {
    const result = yield call(MutationApi.updateLineItems, orderId, orderMenuItems)
    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield put(updateOrderLineItemsSucceeded(result))
      yield put(orderDetailsRequested(orderId))
    } else {
      const errorMessage = result?.data?.orderMutations?.[0]?.applicationMessage ?? 'Line items failed to update'
      Log.error('Update Line Items Failed', { errorMessage, orderId, orderMenuItems })
    }
  } catch (err) {
    const errorMessage = err?.response?.data?.errorMessage ?? 'Line items failed to update'
    Log.error('Update Line Items Failed', { errorMessage, orderId, orderMenuItems })
  }
}

export function* watchUpdateOrderLineItemsSaga() {
  yield takeEvery(updateOrderLineItemsRequested.type, updateOrderLineItems)
}

export function* fetchReceiptDetails(params = {}) {
  const orderId = get(params, 'payload.orderId')
  try {
    const result = yield call(Remote.getReceiptDetails, orderId)
    yield put(receiptDetailsSucceeded(result?.data?.receipt ?? {}))
  } catch (err) {
    yield put(receiptDetailsFailed())
    toast.error('Fetch Receipt details failed')
    Log.error('Fetch Receipt details failed', { orderId, err })
  }
}

export function* watchGetReceiptDetails() {
  yield takeEvery(getReceiptDetails.type, fetchReceiptDetails)
}
